import { LEADS_UNSUPPORTED_MESSAGE, UNASSIGNED_OPTION_VALUE } from 'lib/constants';
import { ConversationMessageType } from 'lib/enum/conversation-message-type.enum';
import { InboxMessageOrigin } from 'lib/enum/inbox-message-origin.enum';
import { MessageDirection } from 'lib/enum/message-direction.enum';
import {
    ConversationMessagePreviewContent,
    InteractiveConversationMessageReplyContent,
    ReceivedEmailMessageContent,
} from 'lib/types/conversation-message-content-type';
import { TemplateComponentButton, TemplateComponentType, TemplateStatus, WaTemplateType } from 'lib/types/wa-template';
import { sortDesc } from 'lib/utils/array.utils';
import { Iri, getUuidFromNullableItem, generateIri, getUuidFromIri } from 'lib/utils/iri';
import { formatTalentPhoneNumber, getTalentFullName } from 'lib/utils/talent.utils';
import dayjs from 'dayjs';
import { useCallback, useMemo, useRef } from 'react';
import {
    BusinessAccountChannel,
    ConversationsMessage,
    ConversationMessagesGroupedByAgentSessionEvent,
    CurrentUser,
    Talent,
    LeadsConversation,
    User,
    ContentTag,
    ConversationLatestMessagePreview,
    TalentEntry,
    MessagingChannel,
} from 'services';
import {
    InboxMessageType,
    SessionEventName,
    type AgentSessionEvent,
    type InboxMessage,
    type TalentEntryMessage,
    type TalentApplicationMessage,
} from 'services/Api/Conversation';
import { ApplicationCreationSource } from 'lib/enum/application-creation-source.enum';
import type { TalentApplication } from 'services/Api/TalentApplications';
import {
    CONVERSATION_LIST_ITEM_ELEMENT_SIZE,
    DEFAULT_PAGE_TO_DISPLAY,
    EndpointUrlParamNames,
    LeadsConversationsFilterName,
    LeadsConversationsFilters,
    LeadsConversationsOrderName,
    LeadsConversationsExistsName,
    OrderDirection,
    WHATSAPP_HOURS_TIME_WINDOW_FOR_RESPONSE,
} from './config';
import { TalentConversationLocation } from 'lib/enum/lead-conversation-location';
import {
    DEFAULT_LABEL_EMAIL_HEIGHT,
    DEFAULT_LABEL_HEIGHT,
    EXPANDED_LABEL_EMAIL_HEIGHT,
    EXPANDED_LABEL_HEIGHT,
    MOBILE_LABEL_HEIGHT,
    Info,
} from 'containers/InboxSendModule/config';
import { getUserDisplayName } from 'lib/utils/user.utils';
import { ResourceKind } from 'lib/enum/resource-kind.enum';
import { MessageStatus } from 'lib/enum/message-status.enum';
import { ChannelType } from 'lib/enum/message-channel.enum';
import { tagIrisToTags } from 'lib/utils/contentTag.utils';
import { AttachmentType } from 'lib/enum/attachment-type.enum';
import InboxMessageUtils from 'lib/utils/inboxMessage.utils';
import { getEnabledChannels } from 'services/Leads/channelSettings';

export const MRWORK_EMAIL_DOMAIN = '@mrwork.nl';
export const WEB_CHAT_SESSION_REQUIRED_MESSAGE =
    'Sending Web chat message is only possible for a conversation that has a Web chat session';
export const WHATSAPP_RECIPIENT_MISSING_MESSAGE =
    'Sending WhatsApp messages is only possible when a valid phone number or WhatsApp username is set.';

export const getTalentDisplayName = (talent: Talent): string => {
    const talentFullName = getTalentFullName(talent);
    return (
        talentFullName ||
        talent?.email ||
        (talent?.phone ? formatTalentPhoneNumber(talent.phone.value) : '') ||
        talent?.whatsappUsername ||
        ''
    );
};

export const checkChannelType = (
    channelId: string,
    availableChannels: BusinessAccountChannel[],
): ChannelType | undefined => {
    return availableChannels.find(availableChannel => availableChannel.id === channelId)?.type;
};

export const getTalentAvatarPlaceholderLetters = (talent: Talent): string => {
    const talentFullName = getTalentFullName(talent);
    if (talentFullName) {
        const names = [talent?.firstName, talent?.lastName].filter(item => !!item);
        if (names.length > 1) {
            return names.shift()!.charAt(0) + names.pop()!.charAt(0);
        } else {
            return talentFullName.charAt(0);
        }
    }
    if (talent?.email) {
        return talent?.email.substring(0, 2);
    }
    if (talent?.phone) {
        return talent?.phone?.value?.slice(-2) || '';
    }
    if (talent?.whatsappUsername) {
        return talent.whatsappUsername.slice(-2);
    }
    return '';
};

const getMessageDisplayType = (channelType: ChannelType | undefined): string => {
    switch (channelType) {
        case ChannelType.Whatsapp:
            return 'WhatsApp message';
        case ChannelType.Sms:
            return 'SMS';
        case ChannelType.Email:
            return 'Email';
        default:
            return channelType || '';
    }
};

export const getInboxDisabilityMessage = (params: {
    talent?: Talent;
    conversationsMessages: ConversationsMessage[];
    openedConversationLocation: TalentConversationLocation | null;
    talentsConsentMessage?: string;
    businessAccountAllChannels?: MessagingChannel[];
    currentChannelType?: ChannelType;
}): Info[] | undefined => {
    const { talent, conversationsMessages, talentsConsentMessage, businessAccountAllChannels, currentChannelType } =
        params;
    const messages: Info[] = [];
    const enabledChannels = getEnabledChannels(businessAccountAllChannels ?? []);
    const hasOnlyWebChatChannels =
        enabledChannels.length > 0 && enabledChannels.every(channel => channel.type === ChannelType.WebChat);
    const webChatSessionMissing = !hasConversationWebChatSession(conversationsMessages);

    const messageDisplayType = getMessageDisplayType(currentChannelType);
    if (currentChannelType === ChannelType.Whatsapp && (!talent || !checkIfTalentCanBeContactedOnWhatsapp(talent))) {
        messages.push({
            text: WHATSAPP_RECIPIENT_MISSING_MESSAGE,
            variant: 'info',
            disableInput: true,
        });
    } else if (currentChannelType === ChannelType.Sms && (!talent || !checkIfTalentHasPhoneDefined(talent))) {
        messages.push({
            text: `Sending ${messageDisplayType} is only possible for Talent that has phone number set`,
            variant: 'info',
            disableInput: true,
        });
    } else if (currentChannelType === ChannelType.Sms && (!talent || !checkIfTalentHasPhoneValid(talent))) {
        messages.push({
            text: `Sending ${messageDisplayType} is only possible for Talent that has valid phone number set`,
            variant: 'info',
            disableInput: true,
        });
    }

    if (currentChannelType === ChannelType.Email && !talent?.email) {
        messages.push({
            text: `Sending ${messageDisplayType} is only possible for Talent that has email set`,
            variant: 'info',
            disableInput: true,
        });
    }

    if (
        (currentChannelType === ChannelType.WebChat && webChatSessionMissing) ||
        (!currentChannelType && hasOnlyWebChatChannels && webChatSessionMissing)
    ) {
        messages.push({
            text: WEB_CHAT_SESSION_REQUIRED_MESSAGE,
            variant: 'info',
            disableInput: true,
        });
    }

    if (checkIfWhatsappTimeWindowClosed(conversationsMessages, businessAccountAllChannels, currentChannelType)) {
        messages.push({
            text: 'There is a 24-hour WhatsApp window to chat. This window has expired. You still can keep in touch by sending a template message.',
            variant: 'info',
            disableInput: true,
        });
    }

    if (params.openedConversationLocation === TalentConversationLocation.Archive) {
        messages.push({
            text: 'This conversation is archived. Sending a message will unarchive it.',
            variant: 'info',
        });
    }

    if (params.openedConversationLocation === TalentConversationLocation.Spam) {
        messages.push({
            text: 'This conversation has been marked as spam.',
            variant: 'warning',
        });
    }

    if (talentsConsentMessage) {
        messages.push({
            variant: 'warning',
            text: talentsConsentMessage,
        });
    }

    return messages.length ? messages : undefined;
};

export const getInboxDisabilityTemplate = (talent?: Talent): string | null => {
    if (!talent || !checkIfTalentCanBeContactedOnWhatsapp(talent)) {
        return WHATSAPP_RECIPIENT_MISSING_MESSAGE;
    }

    return null;
};

function checkIfLatestWhatsappMessageTimeWindowClosed(conversationMessageCreatedAt: string): boolean {
    const hoursPassedSinceLastMessage = dayjs().diff(dayjs(conversationMessageCreatedAt), 'hours', true);
    const timeWindowClosed = hoursPassedSinceLastMessage >= WHATSAPP_HOURS_TIME_WINDOW_FOR_RESPONSE;
    return timeWindowClosed;
}

export function checkIfWhatsappTimeWindowClosed(
    conversationsMessages: ConversationsMessage[],
    businessAccountAllChannels?: MessagingChannel[],
    currentChannelType?: ChannelType,
): boolean {
    const messagesWithDirectionReceived = conversationsMessages.filter(
        conversation => conversation.direction === MessageDirection.Received,
    );
    const messagesSortedByDate = messagesWithDirectionReceived.sort((messageA, messageB) =>
        sortDesc(new Date(messageA.createdAt).getTime(), new Date(messageB.createdAt).getTime()),
    );
    const latestMessageFromWhatsappChannel = messagesSortedByDate.find(message => {
        return checkMessageChannelType(message, businessAccountAllChannels) === ChannelType.Whatsapp;
    });

    if (latestMessageFromWhatsappChannel && currentChannelType === ChannelType.Whatsapp) {
        return checkIfLatestWhatsappMessageTimeWindowClosed(latestMessageFromWhatsappChannel.createdAt);
    } else if (currentChannelType === ChannelType.Whatsapp) {
        return true;
    }

    return false;
}

export const checkIfTalentHasPhoneDefined = (talent: Talent): boolean => !!talent?.phone?.value;

export const checkIfTalentHasPhoneValid = (talent: Talent): boolean => !!talent?.phone?.valid;

export const checkIfTalentCanBeContactedOnWhatsapp = (talent: Talent): boolean =>
    !!talent?.whatsappUsername || (checkIfTalentHasPhoneDefined(talent) && checkIfTalentHasPhoneValid(talent));

export function getMessageSenderDisplayName({
    currentUser,
    senderUser,
    origin,
}: {
    currentUser: CurrentUser;
    senderUser: User | null;
    origin: InboxMessageOrigin;
}): string {
    if (InboxMessageUtils.isSentByConversationalAgent(origin)) {
        return 'AI Agent';
    }

    if (InboxMessageUtils.isSentBySystem(origin)) {
        return 'System';
    }

    if (origin === InboxMessageOrigin.MrworkInbox) {
        const senderDisplayName = senderUser ? getUserDisplayName(senderUser) : '';
        const displayName = currentUser.user.id === senderUser?.id ? 'You' : senderDisplayName || '';
        return displayName;
    }

    return '';
}

export function getSentByCurrentUser({
    currentUser,
    sender,
}: {
    currentUser: CurrentUser;
    sender: Iri | null;
}): boolean {
    return currentUser.user.id === getUuidFromNullableItem(sender);
}

export const resolveDisabled = (status: TemplateStatus): boolean => {
    switch (status) {
        case TemplateStatus.Approved: {
            return false;
        }
        case TemplateStatus.Pending:
        case TemplateStatus.Rejected: {
            return true;
        }
        default:
            return true;
    }
};

export const useListItemRefs = (): {
    setItemRef: (id: string, ref: React.RefObject<HTMLDivElement>) => void;
    getItemRef: (id: string) => HTMLDivElement | null;
} => {
    const listItemRefs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});

    const setItemRef = useCallback((id: string, ref: React.RefObject<HTMLDivElement>) => {
        listItemRefs.current[id] = ref;
    }, []);

    const getItemRef = useCallback((id: string) => {
        return listItemRefs.current[id]?.current || null;
    }, []);

    return {
        setItemRef,
        getItemRef,
    };
};

const getLatestMessageContent = (
    latestMessagePreview: ConversationLatestMessagePreview,
): ConversationMessagePreviewContent | null => {
    if (latestMessagePreview?.unsupported) {
        return {
            text: LEADS_UNSUPPORTED_MESSAGE,
            attachment: null,
        };
    }

    if (latestMessagePreview?.attachmentType) {
        return {
            ...latestMessagePreview,
            attachment: {
                type: latestMessagePreview.attachmentType,
            },
        };
    }

    return latestMessagePreview;
};

export const getLatestMessage = (
    leadsConversation?: LeadsConversation,
):
    | {
          latestMessageDateTime: string;
          latestMessageContent: ConversationMessagePreviewContent | null;
      }
    | {
          latestMessageDateTime: null;
          latestMessageContent: null;
      } => {
    return leadsConversation?.latestMessagePreview
        ? {
              latestMessageDateTime: leadsConversation.latestMessagePreview.createdAt,
              latestMessageContent: getLatestMessageContent(leadsConversation.latestMessagePreview),
          }
        : {
              latestMessageDateTime: null,
              latestMessageContent: null,
          };
};

export const latestMessageToLatestMessagePreview = (
    latestMessage: ConversationsMessage,
): ConversationLatestMessagePreview => {
    const preview = {
        text: null as string | null,
        createdAt: latestMessage.createdAt,
        direction: latestMessage.direction,
        status: 'status' in latestMessage ? latestMessage.status : null,
        channelType: getChannelType(latestMessage.messageType),
        channelId: latestMessage.channel,
        attachmentType: null as AttachmentType | null,
        messageType: latestMessage.messageType,
        options: null as string[] | null,
        unsupported:
            latestMessage.messageType === ConversationMessageType.UnsupportedSent ||
            latestMessage.messageType === ConversationMessageType.UnsupportedReceived,
    };

    if ('message' in latestMessage) {
        if (typeof latestMessage.message === 'object' && latestMessage.message !== null) {
            preview.text =
                latestMessage.message?.subject ||
                latestMessage.message?.text ||
                latestMessage.message?.content?.text ||
                latestMessage.message?.body ||
                (latestMessage.message as InteractiveConversationMessageReplyContent)?.replyText ||
                null;

            if (latestMessage.message.attachment?.type) {
                preview.attachmentType = latestMessage.message.attachment?.type as AttachmentType;
            }
        }
    }

    return preview;
};

const getChannelType = (messageType: ConversationMessageType): ChannelType => {
    switch (messageType) {
        case ConversationMessageType.WhatsappTemplateSent:
        case ConversationMessageType.WhatsappInteractiveSent:
        case ConversationMessageType.WhatsappInteractiveReplyReceived:
        case ConversationMessageType.WhatsappBasicSent:
        case ConversationMessageType.WhatsappBasicReceived:
            return ChannelType.Whatsapp;
        case ConversationMessageType.EmailSent:
        case ConversationMessageType.EmailReceived:
            return ChannelType.Email;
        case ConversationMessageType.SmsSent:
        case ConversationMessageType.SmsReceived:
            return ChannelType.Sms;
        case ConversationMessageType.WebChatSent:
        case ConversationMessageType.WebChatReceived:
            return ChannelType.WebChat;
        default:
            return ChannelType.Sms;
    }
};

export const getInboxInputPlaceholder = ({
    talent,
    conversationsMessages,
    businessAccountAllChannels,
    currentChannelType,
}: {
    talent?: Talent;
    conversationsMessages: ConversationsMessage[];
    businessAccountAllChannels?: MessagingChannel[];
    currentChannelType?: ChannelType;
}): string => {
    return (
        (checkIfWhatsappTimeWindowClosed(conversationsMessages, businessAccountAllChannels, currentChannelType) &&
            talent &&
            checkIfTalentHasPhoneDefined(talent) &&
            checkIfTalentHasPhoneValid(talent) &&
            "You've reached your 24 hour limit on WhatsApp") ||
        'Type your message here'
    );
};

type MessageByAiSessionEvent = (
    conversationsMessages: ConversationsMessage[],
    agentSessionEvents: AgentSessionEvent[],
    talentEntries?: TalentEntry[],
    talentApplications?: TalentApplication[],
) => ConversationMessagesGroupedByAgentSessionEvent[];

const talentEntriesToMessages = (talentEntries: TalentEntry[]): TalentEntryMessage[] =>
    talentEntries.map(entry => ({
        entry,
        id: entry.id,
        createdAt: entry.importedOn ?? entry.externallyCreatedOn,
    }));

const talentApplicationsToMessages = (talentApplications: TalentApplication[]): TalentApplicationMessage[] =>
    talentApplications.map(application => ({
        application,
        id: application.id,
        createdAt: application.externallyCreatedAt ?? application.createdAt,
    }));

export const messagesByAiSessionEvent: MessageByAiSessionEvent = (
    conversationsMessages,
    agentSessionEvents,
    talentEntries,
    talentApplications,
) => {
    const talentEntryMessages = talentEntriesToMessages(talentEntries ?? []);
    const talentApplicationMessages = talentApplicationsToMessages(talentApplications ?? []);

    const AIEvents = [
        ConversationMessageType.AiReceived,
        ConversationMessageType.AiSent,
        ConversationMessageType.AIWhatsappTemplateReceived,
    ];
    const isAiConversationMessage = (message: ConversationsMessage): boolean =>
        AIEvents.includes(message.messageType) ||
        (message.messageType === ConversationMessageType.EmailSent &&
            message.origin === InboxMessageOrigin.ConversationalAgent);
    const aiConversationMessages: ConversationsMessage[] = conversationsMessages.filter(isAiConversationMessage);
    const nonAiConversationMessages: ConversationsMessage[] = conversationsMessages.filter(
        message => !isAiConversationMessage(message),
    );
    const inboxMessages: InboxMessage[] = [
        ...nonAiConversationMessages.map(message => ({
            type: InboxMessageType.Conversation as const,
            message,
        })),
        ...aiConversationMessages.map(message => ({
            type: InboxMessageType.AiConversation as const,
            message,
        })),
        ...agentSessionEvents.map(event => ({
            type: InboxMessageType.AgentSessionEvent as const,
            message: event,
        })),
        ...talentEntryMessages.map(event => ({
            type: InboxMessageType.TalentEntryEvent as const,
            message: event,
        })),
        ...talentApplicationMessages.map(event => ({
            type: InboxMessageType.TalentApplicationEvent as const,
            message: event,
        })),
    ];

    const sortedInboxMessages = inboxMessages.sort((a, b) => {
        const dateA: string = a.message.createdAt;
        const dateB: string = b.message.createdAt;

        const timeA = new Date(dateA).getTime();
        const timeB = new Date(dateB).getTime();

        if (timeA === timeB) {
            return a.type === InboxMessageType.Conversation ? -1 : 1;
        }

        return timeA - timeB;
    });

    const segments: ConversationMessagesGroupedByAgentSessionEvent[] = [];
    let currentSegment: InboxMessage[] = [];
    let segmentId = 1;
    let isInSession = false;

    for (const message of sortedInboxMessages) {
        if (message.type === InboxMessageType.AgentSessionEvent) {
            const event = message.message as AgentSessionEvent;

            if (event.name === SessionEventName.SessionActivated) {
                if (isInSession && currentSegment.length > 0) {
                    segments.push({
                        id: `segment-${segmentId}`,
                        messages: [...currentSegment],
                    });
                    segmentId += 1;
                }
                currentSegment = [message];
                isInSession = true;
            } else if (event.name === SessionEventName.SessionCompleted) {
                currentSegment.push(message);
                if (currentSegment.length > 0) {
                    segments.push({
                        id: `segment-${segmentId}`,
                        messages: [...currentSegment],
                    });
                    segmentId += 1;
                }
                currentSegment = [];
                isInSession = false;
            } else {
                if (isInSession) {
                    currentSegment.push(message);
                } else {
                    segments.push({
                        id: `segment-${segmentId}`,
                        messages: [message],
                    });
                    segmentId += 1;
                }
            }
        } else {
            if (message.type === InboxMessageType.AiConversation || isInSession) {
                currentSegment.push(message);
                isInSession = true;
            } else {
                segments.push({
                    id: `segment-${segmentId}`,
                    messages: [message],
                });
                segmentId += 1;
                isInSession = true;
            }
        }
    }

    if (currentSegment.length > 0) {
        segments.push({
            id: `segment-${segmentId}`,
            messages: currentSegment,
        });
    }

    return segments;
};

type TalentAssignedToCurrentUser = (params: { talent: Talent; currentUser: CurrentUser }) => boolean;
export const talentAssignedToCurrentUser: TalentAssignedToCurrentUser = ({ talent, currentUser }) =>
    getUuidFromNullableItem(talent.assignee) === currentUser.user.id;

export const getOpenedConversationId = (): string => window.location.hash.replace('#', '');

export const closeOpenedConversationId = (): void => {
    window.location.hash = '';
};

type IsOpenedLeadsConversation = (talentId: string) => boolean;
export const isOpenedLeadsConversation: IsOpenedLeadsConversation = (talentId: string) =>
    talentId === getOpenedConversationId();

export const isFilterAllConversationsActive = (
    locationSearch: string,
    currentUser: CurrentUser,
    aiAssistantUserId?: string,
): boolean => {
    const params = new URLSearchParams(locationSearch);
    const noEmptyConversationFilter = params.get(LeadsConversationsFilterName.EmptyConversations) === 'false';
    const locationInboxFilterSelected =
        params.get(LeadsConversationsFilterName.Location) === TalentConversationLocation.Inbox;
    return (
        !isFilterAssignedToMeActive(locationSearch, currentUser) &&
        !isFilterUnassignedActive(locationSearch) &&
        !isFilterTalentpoolActive(locationSearch) &&
        !isFilterContentTagActive(locationSearch) &&
        !isAssignedToOthersActive(currentUser) &&
        !isFilterPrimaryActive(locationSearch) &&
        !isFilterAutomatedActive(locationSearch, aiAssistantUserId!) &&
        !isFilterExpiringSoonActive(locationSearch) &&
        !isFilterAiAgentActive(locationSearch) &&
        noEmptyConversationFilter &&
        locationInboxFilterSelected
    );
};

export const isFilterAssignedToMeActive = (locationSearch: string, currentUser: CurrentUser): boolean => {
    const params = new URLSearchParams(locationSearch);
    const assigneeValue = params.getAll(LeadsConversationsFilterName.Assignee);
    const locationValue = params.get(LeadsConversationsFilterName.Location);
    const filterExists = assigneeValue.includes(currentUser.user.id);
    return filterExists && assigneeValue.length === 1 && locationValue === TalentConversationLocation.Inbox;
};

export const isFilterUnassignedActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const assigneeValue = params.getAll(LeadsConversationsFilterName.Assignee);
    const locationValue = params.get(LeadsConversationsFilterName.Location);
    const filterExists = assigneeValue.includes(UNASSIGNED_OPTION_VALUE);
    return filterExists && assigneeValue.length === 1 && locationValue === TalentConversationLocation.Inbox;
};

export const isFilterEmptyActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.EmptyConversations);
    const locationInbox = params.get(LeadsConversationsFilterName.Location) === TalentConversationLocation.Inbox;
    return filterValue === 'true' && locationInbox;
};

export const isFilterArchiveActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.Location);
    const filterAssigneeValue = params.get(LeadsConversationsFilterName.Assignee);
    return filterValue === TalentConversationLocation.Archive && !filterAssigneeValue;
};

export const isFilterArchiveAssignedToMeActive = (locationSearch: string, currentUser: CurrentUser): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.Location);
    const filterAssigneeValue = params.get(LeadsConversationsFilterName.Assignee);
    return (
        filterValue === TalentConversationLocation.Archive &&
        !!filterAssigneeValue &&
        filterAssigneeValue === currentUser.user.id
    );
};

export const isFilterArchiveUnassignedActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.Location);
    const filterAssigneeValue = params.get(LeadsConversationsFilterName.Assignee);
    return filterValue === TalentConversationLocation.Archive && filterAssigneeValue === UNASSIGNED_OPTION_VALUE;
};

export const isFilterSpamActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.Location);
    const filterAssigneeValue = params.get(LeadsConversationsFilterName.Assignee);
    return filterValue === TalentConversationLocation.Spam && !filterAssigneeValue;
};

export const isFilterSpamAssignedToMeActive = (locationSearch: string, currentUser: CurrentUser): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.Location);
    const filterAssigneeValue = params.get(LeadsConversationsFilterName.Assignee);
    return (
        filterValue === TalentConversationLocation.Spam &&
        !!filterAssigneeValue &&
        filterAssigneeValue === currentUser.user.id
    );
};

export const isFilterSpamUnassignedActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.Location);
    const filterAssigneeValue = params.get(LeadsConversationsFilterName.Assignee);
    return filterValue === TalentConversationLocation.Spam && filterAssigneeValue === UNASSIGNED_OPTION_VALUE;
};

export const isFilterPrimaryActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const assigneeValues = params.getAll(LeadsConversationsFilterName.Assignee);
    const locationValue = params.get(LeadsConversationsFilterName.Location);

    return (
        locationValue === TalentConversationLocation.Inbox &&
        assigneeValues.length > 1 &&
        assigneeValues.includes('unassigned') &&
        assigneeValues.some(value => value !== 'unassigned')
    );
};

export const isFilterAutomatedActive = (locationSearch: string, aiAssistantUserId: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const assigneeValues = params.get(LeadsConversationsFilterName.Assignee);

    return assigneeValues === aiAssistantUserId;
};

export const isFilterAiAgentActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const currentAgentSessionInActiveMode = params.get(LeadsConversationsFilterName.CurrentAgentSessionInActiveMode);
    const locationValue = params.get(LeadsConversationsFilterName.Location);

    return currentAgentSessionInActiveMode === 'true' && locationValue === TalentConversationLocation.Inbox;
};

export const isFilterWhatsappActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const orderParam = params.get(LeadsConversationsOrderName.LatestWhatsappSentMessageAt);
    const locationValue = params.get(LeadsConversationsFilterName.Location);

    return !locationValue && orderParam === OrderDirection.DESC;
};

export const isFilterEmailActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const orderParam = params.get(LeadsConversationsOrderName.LatestEmailSentMessageAt);
    const locationValue = params.get(LeadsConversationsFilterName.Location);

    return !locationValue && orderParam === OrderDirection.DESC;
};

export const isFilterSmsActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const orderParam = params.get(LeadsConversationsOrderName.LatestSmsSentMessageAt);
    const locationValue = params.get(LeadsConversationsFilterName.Location);

    return !locationValue && orderParam === OrderDirection.DESC;
};

export const isFilterAllChannelsActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const orderParam = params.get(LeadsConversationsOrderName.LatestSentMessageAt);
    const locationValue = params.get(LeadsConversationsFilterName.Location);

    return !locationValue && orderParam === OrderDirection.DESC;
};

export const isFilterExpiringSoonActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const expiringSoonValue = params.get(LeadsConversationsFilterName.ExpiringSoonOnWhatsapp);
    const locationValue = params.get(LeadsConversationsFilterName.Location);

    return expiringSoonValue === 'true' && locationValue === TalentConversationLocation.Inbox;
};

export const isFilterTalentpoolActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.Talentpool);
    const locationValue = params.get(LeadsConversationsFilterName.Location);
    return !!filterValue && locationValue === TalentConversationLocation.Inbox;
};

export const isFilterContentTagActive = (locationSearch: string): boolean => {
    const params = new URLSearchParams(locationSearch);
    const filterValue = params.get(LeadsConversationsFilterName.ContentTag);
    const assignedToOthersFilterValue = params.get(LeadsConversationsFilterName.Assignee);
    const talentpoolFilterValue = params.get(LeadsConversationsFilterName.Talentpool);
    const locationValue = params.get(LeadsConversationsFilterName.Location);
    return (
        !!filterValue &&
        locationValue === TalentConversationLocation.Inbox &&
        !assignedToOthersFilterValue &&
        !talentpoolFilterValue
    );
};

export const isAssignedToOthersActive = (currentUser: CurrentUser): boolean => {
    const params = new URLSearchParams(window.location.search);
    const filterValue = params.get(LeadsConversationsFilterName.Assignee);
    const locationValue = params.get(LeadsConversationsFilterName.Location);
    return (
        !!filterValue &&
        locationValue === TalentConversationLocation.Inbox &&
        filterValue !== UNASSIGNED_OPTION_VALUE &&
        filterValue !== currentUser.user.id
    );
};

const getContentTagTitleData = (
    locationSearch: string,
    contentTags: ContentTag[],
): { title: string; subtitle: string } | null => {
    if (!isFilterContentTagActive(locationSearch)) {
        return null;
    }

    const params = new URLSearchParams(locationSearch);
    const contentTagId = params.get(LeadsConversationsFilterName.ContentTag);

    if (!contentTagId) {
        return { title: 'Tags', subtitle: 'Unknown tag' };
    }

    const contentTag = contentTags.find(tag => tag.id === contentTagId);
    const tagName = contentTag?.name || 'Unknown tag';

    const capitalizedTagName = tagName.charAt(0).toUpperCase() + tagName.slice(1);
    return { title: 'Tags', subtitle: capitalizedTagName };
};

const getAssignedToOthersTitleData = (
    currentUser: CurrentUser,
    talentAssignees: User[],
): { title: string; subtitle: string } | null => {
    if (!isAssignedToOthersActive(currentUser)) {
        return null;
    }

    const params = new URLSearchParams(window.location.search);
    const assigneeId = params.get(LeadsConversationsFilterName.Assignee);
    const user = assigneeId ? talentAssignees?.find(user => user.id === assigneeId) : null;

    if (user) {
        const assigneeDisplayName = `${user?.givenName ?? ''} ${user?.familyName ?? ''}`.trim();
        return { title: 'Assigned to', subtitle: assigneeDisplayName };
    }

    return { title: 'Assigned to', subtitle: 'Others' };
};

const getTalentpoolTitleData = (
    locationSearch: string,
    talentpools: any[] = [],
): { title: string; subtitle: string } | null => {
    if (!isFilterTalentpoolActive(locationSearch)) {
        return null;
    }

    const params = new URLSearchParams(locationSearch);
    const talentpoolId = params.get(LeadsConversationsFilterName.Talentpool);

    if (!talentpoolId) {
        return { title: 'Talentpools', subtitle: 'Unknown pool' };
    }

    const talentpool = talentpools.find(pool => pool.id === talentpoolId);
    const poolName = talentpool?.title || 'Unknown pool';

    const capitalizedPoolName = poolName.charAt(0).toUpperCase() + poolName.slice(1);
    return { title: 'Talentpools', subtitle: capitalizedPoolName };
};

export const getConversationsTitleData = (
    locationSearch: string,
    currentUser: CurrentUser,
    talentAssignees: User[],
    contentTags: ContentTag[] = [],
    aiAssistantUserId?: string,
    talentpools: any[] = [],
): { title: string; subtitle: string } => {
    if (isFilterExpiringSoonActive(locationSearch)) {
        return { title: 'Inbox', subtitle: 'Expiring soon' };
    }

    if (isFilterWhatsappActive(locationSearch)) {
        return { title: 'Sent', subtitle: 'WhatsApp' };
    }

    if (isFilterEmailActive(locationSearch)) {
        return { title: 'Sent', subtitle: 'Email' };
    }

    if (isFilterSmsActive(locationSearch)) {
        return { title: 'Sent', subtitle: 'SMS' };
    }

    if (isFilterAllChannelsActive(locationSearch)) {
        return { title: 'Sent', subtitle: 'All channels' };
    }

    if (isFilterContentTagActive(locationSearch)) {
        return getContentTagTitleData(locationSearch, contentTags) || { title: 'Tags', subtitle: 'Unknown tag' };
    }

    if (isFilterTalentpoolActive(locationSearch)) {
        return (
            getTalentpoolTitleData(locationSearch, talentpools) || { title: 'Talent Pool', subtitle: 'Unknown pool' }
        );
    }

    if (isFilterPrimaryActive(locationSearch)) {
        return { title: 'Inbox', subtitle: 'Primary' };
    }

    if (isFilterAutomatedActive(locationSearch, aiAssistantUserId!)) {
        return { title: 'Inbox', subtitle: 'Automated' };
    }

    if (isFilterAiAgentActive(locationSearch)) {
        return { title: 'Inbox', subtitle: 'AI agent' };
    }

    if (isFilterArchiveAssignedToMeActive(locationSearch, currentUser)) {
        return { title: 'Archive', subtitle: 'Assigned to me' };
    }

    if (isFilterArchiveUnassignedActive(locationSearch)) {
        return { title: 'Archive', subtitle: 'Unassigned' };
    }

    if (isFilterArchiveActive(locationSearch)) {
        return { title: 'Archive', subtitle: 'All conversations' };
    }

    if (isFilterSpamAssignedToMeActive(locationSearch, currentUser)) {
        return { title: 'Spam', subtitle: 'Assigned to me' };
    }

    if (isFilterSpamUnassignedActive(locationSearch)) {
        return { title: 'Spam', subtitle: 'Unassigned' };
    }

    if (isFilterSpamActive(locationSearch)) {
        return { title: 'Spam', subtitle: 'All conversations' };
    }

    if (isFilterEmptyActive(locationSearch)) {
        return { title: 'Inbox', subtitle: 'Empty' };
    }

    if (isFilterAssignedToMeActive(locationSearch, currentUser)) {
        return { title: 'Inbox', subtitle: 'Assigned to me' };
    }

    if (isFilterUnassignedActive(locationSearch)) {
        return { title: 'Inbox', subtitle: 'Unassigned' };
    }

    if (isAssignedToOthersActive(currentUser)) {
        return (
            getAssignedToOthersTitleData(currentUser, talentAssignees) || { title: 'Assigned to', subtitle: 'Others' }
        );
    }

    if (isFilterAllConversationsActive(locationSearch, currentUser, aiAssistantUserId)) {
        return { title: 'Inbox', subtitle: 'All conversations' };
    }

    return { title: 'Inbox', subtitle: 'Conversations' };
};

export const getConversationsListTitle = (
    locationSearch: string,
    currentUser: CurrentUser,
    talentAssignees: User[],
    contentTags: ContentTag[] = [],
    aiAssistantUserId?: string,
    talentpools: any[] = [],
): string => {
    const { title, subtitle } = getConversationsTitleData(
        locationSearch,
        currentUser,
        talentAssignees,
        contentTags,
        aiAssistantUserId,
        talentpools,
    );
    return `${title} | ${subtitle}`;
};

export const getInboxItemsPerPage = (): number =>
    Math.max(1, Math.ceil(window.innerHeight / CONVERSATION_LIST_ITEM_ELEMENT_SIZE));

export const InboxFilterSets = {
    allConversations: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: '',
        [LeadsConversationsFilterName.EmptyConversations]: false as boolean | undefined,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    assignedToMe: (
        currentUser: CurrentUser,
    ): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: currentUser.user.id,
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    }),

    unassigned: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: UNASSIGNED_OPTION_VALUE,
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    emptyConversations: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: '',
        [LeadsConversationsFilterName.EmptyConversations]: true,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: undefined,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: undefined,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    archiveAll: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: undefined,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: true,
        [LeadsConversationsFilterName.EmptyConversations]: undefined,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Archive,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    archiveAssignedToMe: (
        currentUser: CurrentUser,
    ): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: currentUser.user.id,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: true,
        [LeadsConversationsFilterName.EmptyConversations]: undefined,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Archive,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    }),

    archiveUnassigned: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: UNASSIGNED_OPTION_VALUE,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: true,
        [LeadsConversationsFilterName.EmptyConversations]: undefined,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Archive,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    spamAll: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: undefined,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: true,
        [LeadsConversationsFilterName.EmptyConversations]: undefined,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Spam,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    spamAssignedToMe: (
        currentUser: CurrentUser,
    ): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: currentUser.user.id,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: true,
        [LeadsConversationsFilterName.EmptyConversations]: undefined,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Spam,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    }),

    spamUnassigned: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: UNASSIGNED_OPTION_VALUE,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: true,
        [LeadsConversationsFilterName.EmptyConversations]: undefined,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Spam,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    assignedToTalentpool: (
        talentpoolId: string,
    ): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: undefined,
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: talentpoolId,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    }),

    includeContentTag: (
        contentTagId: string,
    ): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: undefined,
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: contentTagId,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    }),

    assignedToOthers: (
        userId: string,
    ): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: userId,
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    }),

    primaryFilter: (userId: string): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: ['unassigned', userId],
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: false,
        [LeadsConversationsOrderName.LatestManualMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: '1',
    }),

    automatedFilter: (
        aiUserId: string,
    ): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: aiUserId,
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    }),

    aiAgentFilter: (): Partial<LeadsConversationsFilters> & Record<LeadsConversationsOrderName, any> => ({
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: '',
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: true,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    }),

    whatsappFilter: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: '',
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: undefined,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: '1',
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    emailFilter: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: '',
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: undefined,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: '1',
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    smsFilter: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: '',
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: undefined,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: '1',
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    allChannelsFilter: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: '',
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: undefined,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: undefined,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSentMessageAt]: OrderDirection.DESC,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: undefined,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: '1',
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    expiringSoonFilter: {
        [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
        [LeadsConversationsFilterName.Talent]: '',
        [LeadsConversationsFilterName.Assignee]: '',
        [LeadsConversationsFilterName.EmptyConversations]: false,
        [LeadsConversationsFilterName.IncludeEmptyConversations]: false,
        [LeadsConversationsFilterName.Location]: TalentConversationLocation.Inbox,
        [LeadsConversationsFilterName.Search]: undefined,
        [LeadsConversationsFilterName.Unread]: undefined,
        [LeadsConversationsFilterName.Status]: undefined,
        [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
        [LeadsConversationsFilterName.ContentTag]: undefined,
        [LeadsConversationsFilterName.Talentpool]: undefined,
        [LeadsConversationsFilterName.ExpiringSoonOnWhatsapp]: true,
        [LeadsConversationsFilterName.CurrentAgentSessionInActiveMode]: undefined,
        [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsOrderName.LatestWhatsappReceivedMessageAt]: OrderDirection.ASC,
        [LeadsConversationsOrderName.Id]: OrderDirection.ASC,
        [LeadsConversationsExistsName.LatestSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestWhatsappSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestEmailSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestSmsSentMessageAt]: undefined,
        [LeadsConversationsExistsName.LatestManualMessageAt]: undefined,
    },

    goToConversation: (
        location: TalentConversationLocation,
        isEmpty: boolean,
    ): Partial<LeadsConversationsFilters & Record<LeadsConversationsOrderName, any>> => {
        const filterIncludeEmpty = location !== TalentConversationLocation.Inbox ? true : undefined;

        const filterEmpty = filterIncludeEmpty ? undefined : location === TalentConversationLocation.Inbox && isEmpty;

        return {
            [LeadsConversationsFilterName.Page]: DEFAULT_PAGE_TO_DISPLAY,
            [LeadsConversationsFilterName.Talent]: '',
            [LeadsConversationsFilterName.Assignee]: '',
            [LeadsConversationsFilterName.EmptyConversations]: filterEmpty,
            [LeadsConversationsFilterName.IncludeEmptyConversations]: filterIncludeEmpty,
            [LeadsConversationsFilterName.Location]: location,
            [LeadsConversationsFilterName.Search]: undefined,
            [LeadsConversationsFilterName.Unread]: undefined,
            [LeadsConversationsFilterName.Status]: undefined,
            [LeadsConversationsFilterName.ItemsPerPage]: getInboxItemsPerPage(),
            [LeadsConversationsFilterName.ContentTag]: undefined,
            [LeadsConversationsFilterName.Talentpool]: undefined,
            [LeadsConversationsOrderName.LatestManualMessageAt]: undefined,
            [LeadsConversationsOrderName.LatestMessageAt]: undefined,
            [LeadsConversationsOrderName.LatestSentMessageAt]: undefined,
            [LeadsConversationsOrderName.LatestWhatsappSentMessageAt]: undefined,
            [LeadsConversationsOrderName.LatestEmailSentMessageAt]: undefined,
            [LeadsConversationsOrderName.LatestSmsSentMessageAt]: undefined,
            [LeadsConversationsOrderName.Id]: undefined,
        };
    },
};

export const getLocationFilter = (): TalentConversationLocation | null =>
    new URLSearchParams(window.location.search).get(
        LeadsConversationsFilterName.Location,
    ) as TalentConversationLocation;

export const getAssigneeFilter = (): string | null =>
    new URLSearchParams(window.location.search).get(LeadsConversationsFilterName.Assignee) as string;

export const browserBarParamNamesToEndpointUrlParamNames = (
    browserBarUrl: string,
    valueProcessorsBeforeSend?: Record<string, (value: any) => any>,
): URLSearchParams => {
    const endpointUrlParamNames = new URLSearchParams(`?${LeadsConversationsFilterName.Pagination}=true`);
    new URLSearchParams(browserBarUrl).forEach((paramValue, paramName) => {
        const endpointParamName = EndpointUrlParamNames[paramName as keyof typeof EndpointUrlParamNames];
        const valueProcessed = valueProcessorsBeforeSend?.[paramName]
            ? valueProcessorsBeforeSend?.[paramName](paramValue)
            : paramValue;
        if (endpointParamName) {
            endpointUrlParamNames.append(endpointParamName, valueProcessed);
        }
    });
    return endpointUrlParamNames;
};

export const createMessageByTemplate = (
    template: WaTemplateType,
    currentUser: CurrentUser,
    channelIri: string,
): ConversationsMessage => {
    let buttons: TemplateComponentButton[] = [];
    let body: string = '';
    template?.components.forEach(component => {
        if (component.type === TemplateComponentType.Body) {
            body = component.text || '';
        }
        if (component.type === TemplateComponentType.Buttons) {
            buttons = component.buttons || (undefined as any);
        }
    });

    return {
        sender: generateIri(ResourceKind.User, currentUser.user.id),
        status: MessageStatus.Sent,
        origin: InboxMessageOrigin.BirdFlow,
        conversation: 'conversation_placeholder',
        createdAt: new Date().toISOString(),
        id: 'message_id_placeholder',
        channel: channelIri,
        direction: MessageDirection.Sent,
        messageType: ConversationMessageType.WhatsappTemplateSent,
        message: {
            body,
            buttons,
            text: undefined,
            attachment: undefined,
        },
    } as unknown as ConversationsMessage;
};

export const checkMessageChannelType = (
    message: ConversationsMessage,
    businessAccountAllChannels?: MessagingChannel[],
): ChannelType | undefined =>
    (businessAccountAllChannels ?? []).find(channel => {
        return getUuidFromIri(channel.id) === getUuidFromIri(message.channel);
    })?.type;

export const hasConversationWebChatSession = (conversationsMessages: ConversationsMessage[]): boolean =>
    conversationsMessages.some(
        message =>
            message.messageType === ConversationMessageType.WebChatSent ||
            message.messageType === ConversationMessageType.WebChatReceived,
    );

export const getAvailableConversationChannels = (
    channels: MessagingChannel[],
    conversationsMessages: ConversationsMessage[],
): MessagingChannel[] => {
    const enabledChannels = getEnabledChannels(channels);

    if (hasConversationWebChatSession(conversationsMessages)) {
        return enabledChannels;
    }

    return enabledChannels.filter(channel => channel.type !== ChannelType.WebChat);
};

export const getDefaultLabelHeight = (type: ChannelType, isMobile = false): number => {
    if (type === ChannelType.Email) {
        return DEFAULT_LABEL_EMAIL_HEIGHT;
    }
    if (isMobile) {
        return MOBILE_LABEL_HEIGHT;
    }
    return DEFAULT_LABEL_HEIGHT;
};

export const getExpandedLabelHeight = (type: ChannelType, isMobile = false): number => {
    if (type === ChannelType.Email) {
        return EXPANDED_LABEL_EMAIL_HEIGHT;
    }
    if (isMobile) {
        return MOBILE_LABEL_HEIGHT;
    }
    return EXPANDED_LABEL_HEIGHT;
};

export const getEmailReceiverDisplayName = (receivers: ReceivedEmailMessageContent['receivers']): string => {
    return receivers
        .map(receiver => {
            const receiverName = receiver.name ?? '';
            const receiverAddress = receiverName ? `(${receiver.address})` : receiver.address;
            const divider = receiverName ? ' ' : '';
            return `${receiverName}${divider}${receiverAddress}`;
        })
        .join(', ');
};

export const useTalentContentTags = (contentTags: ContentTag[], talent?: Talent): ContentTag[] | null =>
    useMemo(() => (talent ? tagIrisToTags(talent.contentTags, contentTags) : null), [talent, contentTags]);

export const findAiAssistantUser = (usersDict: Record<string, User>): User | null => {
    const aiUsers = Object.values(usersDict).filter(user => {
        const email = user.email?.toLowerCase() || '';
        return email.includes(MRWORK_EMAIL_DOMAIN) && email.includes('automation');
    });

    if (aiUsers.length === 0) {
        return null;
    }

    return aiUsers.sort((a, b) => a.id.localeCompare(b.id))[0];
};

type ActiveAgentSessionLike = { agentId: string; activationContext: string | null; mode: string } | null;

const MANUAL_AGENT_ACTIVATION_CHANNELS = [ChannelType.Whatsapp, ChannelType.Sms, ChannelType.Email];

const getEnabledManualAgentActivationChannelTypes = (
    businessAccountAllChannels?: MessagingChannel[],
): ChannelType[] => {
    const enabledChannels = getEnabledChannels(businessAccountAllChannels ?? []);

    return MANUAL_AGENT_ACTIVATION_CHANNELS.filter(channelType =>
        enabledChannels.some(channel => channel.type === channelType),
    );
};

const hasManualEmailActivationOption = (
    talent: Talent | undefined,
    businessAccountAllChannels?: MessagingChannel[],
): boolean => {
    const enabledChannelTypes = getEnabledManualAgentActivationChannelTypes(businessAccountAllChannels);

    return !!talent?.email && enabledChannelTypes.includes(ChannelType.Email);
};

const getActivationChannelMessageLabel = (channelType: ChannelType): string => {
    switch (channelType) {
        case ChannelType.Whatsapp:
            return 'WhatsApp template message';
        case ChannelType.Sms:
            return 'SMS message';
        case ChannelType.Email:
            return 'Email message';
        default:
            return 'message';
    }
};

const formatActivationChannelMessageLabels = (channelTypes: ChannelType[]): string => {
    const labels = channelTypes.map(getActivationChannelMessageLabel);

    if (labels.length === 0) {
        return 'an inbox message';
    }

    if (labels.length === 1) {
        return labels[0];
    }

    if (labels.length === 2) {
        return labels.join(' or ');
    }

    return `${labels.slice(0, -1).join(', ')}, or ${labels[labels.length - 1]}`;
};

const getSendMessageToEnableAgentActivationText = (channelTypes: ChannelType[]): string =>
    `Send ${formatActivationChannelMessageLabels(channelTypes)} to enable agent activation`;

export const shouldShowManualAiActivationTrigger = ({
    hasAiHubLicense,
    activeAgentSession,
    enabledAgentsCount,
    talent,
    businessAccountAllChannels,
}: {
    hasAiHubLicense: boolean;
    activeAgentSession: ActiveAgentSessionLike;
    enabledAgentsCount: number;
    talent?: Talent;
    businessAccountAllChannels?: MessagingChannel[];
}): boolean => {
    if (!hasAiHubLicense || activeAgentSession || enabledAgentsCount === 0 || !talent) {
        return false;
    }

    const enabledChannelTypes = getEnabledManualAgentActivationChannelTypes(businessAccountAllChannels);
    const canUseWhatsappChannel =
        enabledChannelTypes.includes(ChannelType.Whatsapp) && checkIfTalentCanBeContactedOnWhatsapp(talent);
    const canUseSmsChannel = enabledChannelTypes.includes(ChannelType.Sms) && checkIfTalentHasPhoneDefined(talent);
    const canUsePhoneBasedChannel = canUseWhatsappChannel || canUseSmsChannel;
    const hasEmailChannel = hasManualEmailActivationOption(talent, businessAccountAllChannels);

    return canUsePhoneBasedChannel || hasEmailChannel;
};

export const canActivateAgentManually = (
    conversationsMessages: ConversationsMessage[],
    activeAgentSession: ActiveAgentSessionLike,
    businessAccountAllChannels?: MessagingChannel[],
    talent?: Talent,
): boolean => {
    if (activeAgentSession) {
        return false;
    }

    if (hasManualEmailActivationOption(talent, businessAccountAllChannels)) {
        return true;
    }

    const sortedMessages = [...conversationsMessages].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    const latestMessage = sortedMessages[0];

    if (!latestMessage) {
        return false;
    }

    const enabledChannelTypes = getEnabledManualAgentActivationChannelTypes(businessAccountAllChannels);
    const latestMessageChannelType = checkMessageChannelType(latestMessage, businessAccountAllChannels);
    const isValidChannelType = !!latestMessageChannelType && enabledChannelTypes.includes(latestMessageChannelType);

    if (!isValidChannelType) {
        return false;
    }

    if (latestMessageChannelType === ChannelType.Email) {
        return true;
    }

    const messageTime = dayjs(latestMessage.createdAt);
    const now = dayjs();
    const hoursPassed = now.diff(messageTime, 'hours', true);

    if (latestMessage.direction === MessageDirection.Received) {
        const maxHours = 23 + 50 / 60; // 23h50m = 23.833 hours
        return hoursPassed <= maxHours;
    }

    if (latestMessage.direction === MessageDirection.Sent) {
        return hoursPassed <= 12;
    }

    return false;
};

export const getActivationDisabledReason = (
    conversationsMessages: ConversationsMessage[],
    activeAgentSession: ActiveAgentSessionLike,
    businessAccountAllChannels?: MessagingChannel[],
    talent?: Talent,
): string | undefined => {
    if (canActivateAgentManually(conversationsMessages, activeAgentSession, businessAccountAllChannels, talent)) {
        return undefined;
    }

    if (activeAgentSession) {
        return 'Agent session is already active';
    }

    const enabledChannelTypes = getEnabledManualAgentActivationChannelTypes(businessAccountAllChannels);
    const hasWhatsappEnabled = enabledChannelTypes.includes(ChannelType.Whatsapp);
    const hasSmsEnabled = enabledChannelTypes.includes(ChannelType.Sms);
    const hasEmailEnabled = enabledChannelTypes.includes(ChannelType.Email);

    const sortedMessages = [...conversationsMessages].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    );

    const latestMessage = sortedMessages[0];

    if (!latestMessage) {
        return getSendMessageToEnableAgentActivationText(enabledChannelTypes);
    }

    const latestMessageChannelType = checkMessageChannelType(latestMessage, businessAccountAllChannels);

    if (latestMessageChannelType === ChannelType.Whatsapp) {
        if (hasWhatsappEnabled) {
            return getSendMessageToEnableAgentActivationText([ChannelType.Whatsapp]);
        }
        return getSendMessageToEnableAgentActivationText(enabledChannelTypes);
    }

    if (latestMessageChannelType === ChannelType.Sms) {
        if (hasSmsEnabled) {
            return getSendMessageToEnableAgentActivationText([ChannelType.Sms]);
        }
        return getSendMessageToEnableAgentActivationText(enabledChannelTypes);
    }

    if (latestMessageChannelType === ChannelType.Email) {
        if (hasEmailEnabled) {
            return undefined;
        }
        return getSendMessageToEnableAgentActivationText(enabledChannelTypes);
    }

    return getSendMessageToEnableAgentActivationText(enabledChannelTypes);
};
